import { useEffect, useState } from 'react'
import { Alert, AlertTitle, Box, Button, Stack } from '@mui/material'
import { useRouter } from 'next/navigation'
import CloseIcon from '@mui/icons-material/Close'

import { ROLES } from '@/shared/constants'
import { useAppSelector } from '@/store/hooks'
import useStripeApi from '@/hooks/useStripeApi'
import { BillingStatus } from '@/models/stripe'

export default function FailedPaymentAlert() {
  const { user } = useAppSelector((state) => state.auth)
  const [isVisible, setIsVisible] = useState(false)
  const [billingStatus, setBillingStatus] = useState<BillingStatus>()
  const router = useRouter()
  const { createCustomerPortal, getBillingStatus } = useStripeApi()

  useEffect(() => {
    if (user?.role?.id !== ROLES.COMPANY_ADMIN) return

    let active = true
    const refresh = async () => {
      const status = await getBillingStatus()
      if (!active || !status) return
      setBillingStatus(status)
      setIsVisible(status.status !== 'current')
    }
    void refresh()
    const interval = window.setInterval(refresh, 60_000)
    return () => {
      active = false
      window.clearInterval(interval)
    }
  }, [
    getBillingStatus,
    user?.role?.id,
    user?.company?.id,
    user?.company?.status,
  ])

  const goToCustomerPortalUrl = async () => {
    if (user && user.company?.stripeCustomerId) {
      const data = await createCustomerPortal()
      if (data) {
        router.push(data.url)
      }
    }
  }

  const handleClose = () => {
    setIsVisible(false)
    setTimeout(() => {
      setIsVisible(billingStatus?.status !== 'current')
    }, 60_000)
  }

  if (!billingStatus || billingStatus.status === 'current') return null
  if (!isVisible) return null

  const dueAt = billingStatus.dueAt
    ? new Date(billingStatus.dueAt).toLocaleDateString('pt-BR')
    : null
  const graceEndsAt = billingStatus.graceEndsAt
    ? new Date(billingStatus.graceEndsAt).toLocaleDateString('pt-BR')
    : null
  const boletoOpen = billingStatus.status === 'boleto_open'

  return (
    <Alert
      severity={boletoOpen ? 'info' : 'warning'}
      sx={{
        borderRadius: 0,
        position: 'sticky',
        top: 0,
        zIndex: 1300,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 1,
        px: 2,
      }}
      action={
        <Box sx={{ display: 'flex' }}>
          {billingStatus.boletoUrl && (
            <Button
              variant="contained"
              color="inherit"
              size="small"
              sx={{ mr: 1 }}
              href={billingStatus.boletoUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Acessar boleto
            </Button>
          )}
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            sx={{ mr: 1 }}
            onClick={goToCustomerPortalUrl}
          >
            Gerenciar plano
          </Button>
          <Button color="inherit" size="small" onClick={handleClose}>
            <CloseIcon />
          </Button>
        </Box>
      }
      slotProps={{
        message: {
          sx: {
            display: 'flex',
            alignItems: 'center',
          },
        },
      }}
    >
      <Stack spacing={0.5}>
        <AlertTitle>
          {boletoOpen ? 'Cobrança em aberto' : 'Pagamento pendente'}
        </AlertTitle>
        {boletoOpen
          ? `Seu boleto está disponível${dueAt ? ` e vence em ${dueAt}` : ''}.`
          : `O boleto venceu${dueAt ? ` em ${dueAt}` : ''}. Regularize o pagamento${graceEndsAt ? ` até ${graceEndsAt}` : ''} para evitar a restrição do acesso.`}
      </Stack>
    </Alert>
  )
}
